// Room 2's calls (money in: the price lists and checkouts), through the doorway (doorway.ts). Each sends what its
// screen sent before it moved here (tests/billing-api.test.mjs), but for the `discount` the gems page used to send,
// which v1 has ignored since 12 October 2026 (it decides half price itself) and v2 refuses: the browser no longer
// says who pays half. v1's side is its payments host (`{ payments: path }`), as before.
//
// With room 2's switches on, v2 answers in v1's shapes (billing-v2.ts): the price lists and card checkouts by the
// `billing` area's switch, crypto checkouts by the `crypto` area's, as crypto follows card payments behind its own
// flag (codes go with crypto: ai_app_backend plan.pm, r2-rollout-crypto). Both are off for everyone today.
import {
  V1_API,
  call,
  lazySide,
  onlyOnV2,
  signedIn,
  signedInJson,
} from './doorway.ts'
import {
  RECEIPT_DETAILS_PATH,
  saveReceiptDetails as saveDetails,
} from '../receipt-details.js'
import type { Area, V1Path, V2Side } from './doorway.ts'

type Sides = typeof import('./billing-v2.ts')

/**
 * A call's v2 side, as `use` builds it from billing-v2.ts, loaded only when the switch puts the visitor on v2. When
 * that module fails to load, the call goes to v1 at `path`, as it would have with the switch off.
 */
const onV2 = (path: V1Path, use: (loaded: Sides) => V2Side): V2Side =>
  lazySide(() => import('./billing-v2.ts'), use, path)

const areaOf = (provider: 'card' | 'crypto'): Area =>
  provider === 'card' ? 'billing' : 'crypto'

/**
 * The gems page's prices for this person: `{ half_price, in_test, packs: [{ gem_id, gems, list_price, price }] }`,
 * and on v2 `sale` (null, or the sale a price comes from). Signed out (no token), the list prices, from v1.
 */
export const gemPrices = (token: string | null) => {
  const path = { payments: '/api/gem-prices' }
  return call(
    'billing',
    path,
    { headers: token ? signedIn(token) : {} },
    token
      ? { token, side: onV2(path, (loaded) => loaded.gemPrices) }
      : undefined,
  )
}

/**
 * Starts buying a gem pack (v1's `gem_id`): by card, `{ gateway_url }`, Centrobill's payment page; by crypto,
 * `{ checkout_url }`, NOWPayments' invoice. The price is the server's; nothing in the request sets it.
 */
export const buyGems = (
  token: string,
  gemId: string,
  provider: 'card' | 'crypto',
) => {
  const path = {
    payments:
      provider === 'card'
        ? '/api/centrobill/purchase'
        : '/api/nowpayments-buy-gems',
  }
  return call(
    areaOf(provider),
    path,
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ gem_id: gemId }),
    },
    {
      token,
      side: onV2(
        path,
        (loaded) => (t, init) => loaded.checkout(t, init, { gemId, provider }),
      ),
    },
  )
}

/**
 * The upgrade page's plans from v2's catalogue (`{ sale, plans }`, billing-v2.ts `v1Plans`) for a visitor the
 * `billing` switch puts on v2; null for everyone else, signed out included: the page keeps plan-facts.js, as v1 has no
 * call that answers prices for plans.
 */
export const planPrices = (token: string | null) =>
  onlyOnV2(
    'billing',
    token,
    async () => (await import('./billing-v2.ts')).plans,
  )

/**
 * Starts a plan by card (Centrobill): `{ gateway_url }`. `plan` is v1's name as the page has it ("Studio" or "Pro"
 * for Pro), `cycle` monthly or yearly, `abValue` v1's own experiment arm, which v2 doesn't take. Change plan is the
 * same call, as on v1: a new subscription (ai_app_backend decision 0050).
 */
export const buyPlanByCard = (
  token: string,
  plan: string,
  cycle: 'monthly' | 'yearly',
  abValue?: string,
) => {
  const path = { payments: '/api/centrobill/purchase' }
  return call(
    'billing',
    path,
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ plan, cycle, ab_value: abValue }),
    },
    {
      token,
      side: onV2(
        path,
        (loaded) => (t, init) =>
          loaded.checkout(t, init, { plan, term: cycle, provider: 'card' }),
      ),
    },
  )
}

/** Starts a month of a plan by crypto (NOWPayments' invoice): `{ checkout_url }`, as v1's page always asked. */
export const buyPlanByCrypto = (token: string, plan: string) => {
  const path = { payments: '/api/nowpayments-subscribe' }
  return call(
    'crypto',
    path,
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ plan, cycle: 'monthly' }),
    },
    {
      token,
      side: onV2(
        path,
        (loaded) => (t, init) =>
          loaded.checkout(t, init, {
            plan,
            term: 'monthly',
            provider: 'crypto',
          }),
      ),
    },
  )
}

// Settings (card R2-22): the order history, a receipt, the receipt details, codes; and the declined banner.

const SIGNED_IN_JSON = (token: string) =>
  signedIn(token, {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  })

/** Settings' order history: v1's card orders, `[{ order_id, created_at, item_descriptor, status, receipt_available }]`. */
export const orderHistory = (token: string) =>
  call(
    'billing',
    '/api/order_history',
    { method: 'GET', headers: SIGNED_IN_JSON(token) },
    {
      token,
      side: onV2('/api/order_history', (loaded) => loaded.orders),
    },
  )

/** A card order's receipt, a PDF, by the `order_id` the order history gave. */
export const downloadReceipt = (token: string, orderId: string) => {
  const path = `/api/download_receipt?order_id=${orderId}`
  return call(
    'billing',
    path,
    {
      method: 'GET',
      headers: signedIn(token, { Accept: 'application/pdf' }),
    },
    {
      token,
      side: onV2(
        path,
        (loaded) => (t, init) => loaded.receipt(t, init, orderId),
      ),
    },
  )
}

/** The details printed on the person's card receipts, in v1's `{ custom_receipt_details }`. */
export const getReceiptDetails = (token: string) =>
  call(
    'billing',
    '/api/get_custom_receipt_details',
    { method: 'GET', headers: SIGNED_IN_JSON(token) },
    {
      token,
      side: onV2(
        '/api/get_custom_receipt_details',
        (loaded) => loaded.receiptDetails,
      ),
    },
  )

/**
 * Saves the receipt details in a POST body (src/lib/receipt-details.js), through the doorway; v1's old GET is asked
 * only after v1 answers 405, as before.
 */
export const saveReceiptDetails = (
  token: string,
  details: Record<string, string>,
) =>
  saveDetails({
    baseUrl: V1_API,
    idToken: token,
    details,
    fetchImpl: (url: RequestInfo | URL, init: RequestInit = {}) =>
      init.method === 'POST'
        ? call('billing', RECEIPT_DETAILS_PATH, init, {
            token,
            side: onV2(
              RECEIPT_DETAILS_PATH,
              (loaded) => (t, i) => loaded.saveReceiptDetails(t, i, details),
            ),
          })
        : fetch(url, init),
  })

/** Redeems a gift or reseller code (v1's `key`); a refusal answers `{ error }`, which settings shows. */
export const redeemCode = (token: string, code: string) => {
  const path = { payments: '/api/redeem_code' }
  return call(
    'crypto',
    path,
    {
      method: 'POST',
      headers: SIGNED_IN_JSON(token),
      body: JSON.stringify({ key: code }),
    },
    {
      token,
      side: onV2(path, (loaded) => (t, init) => loaded.redeem(t, init, code)),
    },
  )
}

/**
 * Whether v2 shows this person the declined-payment banner (`{ declined }`), for a visitor the `billing` switch
 * puts on v2; null for everyone else, who keep v1's `centro_decline` from their account document.
 */
export const declinedNotice = (token: string | null) =>
  onlyOnV2(
    'billing',
    token,
    async () => (await import('./billing-v2.ts')).declined,
  )

/** Hides the declined-payment banner, when the person closes it or follows it. */
export const clearDecline = (token: string) =>
  call(
    'billing',
    '/api/clear_centro_decline',
    { method: 'POST', headers: signedInJson(token) },
    {
      token,
      side: onV2(
        '/api/clear_centro_decline',
        (loaded) => loaded.dismissDeclined,
      ),
    },
  )
